'use strict'

/* global dataLayer */

import zonedDate from '../utils/zoned-date';
import { format } from 'date-fns';
import { debounce, sortBy, uniq } from 'lodash';
import Promise from 'promise-polyfill';
import { fetch as fetchPolyfill } from 'whatwg-fetch';
import { loadComponent } from './utils/load-component';
import { getDigitalPerformances } from '../services/digital-inventory';

loadComponent(
    'digital-archive-search',
    {
        props: [
            'engineKey',
            'searchParam',
            'filterParam',
            'searchPageUrl',
        ],
        data: function () {
            return {
                page: 1,
                query: '',
                filter: 'all',
                resultsPerPage: 100,
                inputField: null,
                results: [],
                ViewModel: {
                    SearchQuery: '',
                    Loading: true,
                    TotalResults: 0,
                    Results: [],
                    UpcomingSlider: [],
                    Performances: [],
                    Tabs: [],
                    Pagination: {
                        prev: false,
                        pages: [],
                        next: false,
                    },
                    SearchSuggestions: [],
                    AllFilteredResults: [],
                }
            };
        },
        beforeMount: function () {
            this.$root.$on('DigitalArchiveFilters.filtersUpdated', this.filtersChanged);
            const search = new URLSearchParams(document.location.search);
            this.query = search.has(this.searchParam) ? search.get(this.searchParam) : '';
            this.page = parseInt(search.get('page'), 10) || 1;

            getDigitalPerformances()
            .then(data => {
                this.ViewModel.Performances = data
                .filter(ev => ev.ShowInArchiveFilter)
                .map(event => {
                    const ret = {
                        ...event
                    };

                    ret.Date = ret.first_aired ? format(zonedDate(ret.first_aired), 'MMM dd, yyyy') : null;

                    switch(event.type) {
                        case 'audio':
                            ret.Type = 'Audio';
                            break;
                        case 'video':
                            ret.Type = 'Video';
                            break;
                        default:
                            break;
                    }

                    ret.Filters = {
                        Media:  [
                            {
                                ID: ret.Type,
                                Label: ret.Type,
                            }
                        ],
                        Type: [{
                            ID: ret.Category,
                            Label: ret.Category,
                        }],
                    };

                    return ret;
                });

                this.updateFilters();
            })
            .then(() => {
                this.ViewModel.SearchQuery = this.query;
                this.updateSearch(false);
            })
            .finally(() => {
                this.ViewModel.Loading = false;
            })
            .catch(error => {
                console.log(error);
            })
        },
        mounted: function () {
            if (this.$el) {
                this.inputField = this.$el.querySelector('form input[name="q"]');
                this.inputField.addEventListener('keyup', event => {
                    if (event.keyCode !== 13) { // Exclude Enter key - prevents suggestions coming up after you do a manual search
                      this.getSuggestionsListener()(event);
                    }
                  });
                  document.addEventListener('click', this.handleDocumentClick);
            }
        },
        methods: {
            handleDocumentClick(event) {
                if (!this.$el.querySelector('.digital-archive__form').contains(event.target)) {
                  // Clicked outside of search, clear suggestions
                    this.ViewModel.SearchSuggestions = [];
                }
            },
            showMoreClicked: function () {
                this.page++;
                this.updateResults();
            },
            performanceClicked: function (perf) {
                this.$root.$emit('CalendarPopup.scrollToPerformance', perf);
            },
            updateFilters: function() {
                const events = this.query ? this.results : this.ViewModel.Performances;
                    let filters = [
                        {
                            ID: 'Media',
                            Name: 'Media Type',
                            Options: [],
                            Order: -2,
                        },
                        {
                            ID: 'Type',
                            Name: 'Performance Type',
                            Options: [],
                            Order: -1,
                        },
                    ];

                    events.reduce((filters, event) => {
                        // Media
                        const mediaFilter = filters[0];
                        if (event.Filters.Media) {
                            event.Filters.Media.forEach(media => {
                                const opt = mediaFilter.Options.find(item => item.ID === media.ID);
                                if (!opt) {
                                    mediaFilter.Options.push({
                                        Value: media.ID,
                                        ID: media.ID,
                                        Label: media.Label
                                    });
                                }
                            });
                        }

                        // Type
                        const typeFilter = filters[1];

                        if (event.Filters.Type) {
                            event.Filters.Type = event.Filters.Type.filter(type => type.ID !== null)
                            event.Filters.Type.forEach(type => {
                                const opt = typeFilter.Options.find(item => item.ID === type.ID);
                                if (!opt) {
                                    typeFilter.Options.push({
                                        Value: type.ID,
                                        ID: type.ID,
                                        Label: type.Label,
                                        Order: event.performance_type_order
                                    });
                                }
                            });
                        }

                        event.keywords.forEach(kw => {
                            const id = kw.type.replace(/[\-\s]/, '_');
                            let filter = filters.find(f => f.ID === id);
                            if (!filter) {
                                filter = {
                                    ID: id,
                                    Name: kw.type,
                                    Options: [],
                                    Order: kw.type_order,
                                };
                                filters.push(filter);
                            }

                            if (!filter.Options.find(o => o.ID === kw.value)) {
                                filter.Options.push({
                                    Value: kw.value,
                                    ID: kw.value,
                                    Label: kw.value,
                                    Order: kw.order
                                });
                            }

                            if (!event.Filters[id]) {
                                event.Filters[id] = [];
                            }

                            event.Filters[id].push({
                                ID: kw.value,
                                Label: kw.value,
                            });
                        });

                        return filters;
                    }, filters);

                filters.sort((a, b) => a.Order - b.Order);
                filters.forEach(filter => filter.Options.sort((a, b) => a.Order - b.Order));

                this.$root.$emit('DigitalArchive.updateFilters', filters);
            },
            filtersChanged: function(filter) {
                this.page = 1;

                const perfs = this.query ? this.results : this.ViewModel.Performances;

                this.ViewModel.AllFilteredResults = perfs.filter(event => {
                    return Object.keys(filter).every(filterKey => {
                        // Not in the filter so just include
                        if (!filter[filterKey].length) {
                            return true;
                        }

                        // Check if the event has ALL selected filters for this category
                        return filter[filterKey].every(selectedFilterId => {
                            if (!event.Filters[filterKey]) {
                                return false;
                            }

                            return event.Filters[filterKey].some(
                                filterItem => filterItem.ID === selectedFilterId
                            );
                        });
                    });
                });

                this.updateResults();
            },
            updateResults: function () {
                // If we're running a search, let swiftype sort by relevance
                if (this.query) {
                    this.ViewModel.Results = this.ViewModel.AllFilteredResults
                        .slice(0, this.resultsPerPage * this.page);
                // otherwise we can sort by first_aired in descending order
                } else {
                    this.ViewModel.Results = this.ViewModel.AllFilteredResults
                        .sort((a, b) => zonedDate(b.first_aired) - zonedDate(a.first_aired))
                        .slice(0, this.resultsPerPage * this.page);
                }

                this.$root.$emit('CalendarPopup.updatePerformances', this.ViewModel.Results);
                this.ViewModel.SearchSuggestions = [];
            },
            getSuggestionsListener: function () {
                return debounce(() => {
                    this.getSuggestions(this.inputField.value)
                        .then(this.processSuggestions)
                        .then(this.updateSuggestionList);
                }, 250);
            },
            getSuggestions: function (query) {
                const url = new URL('https://search-api.swiftype.com/api/v1/public/engines/search.json');
                url.searchParams.append('engine_key', this.engineKey);
                url.searchParams.append('q', query);
                url.searchParams.append('spelling', 'always');

                return fetchPolyfill(url.toString())
                    .then(res => res.json())
                    .finally(() => {
                        const searchQuery = new URLSearchParams(document.location.search);
                        searchQuery.set(this.searchParam, query);
                        if (this.page > 1) {
                            searchQuery.set('page', this.page);
                        } else {
                            searchQuery.delete('page');
                        }
                        const newURL = `${window.location.origin}${window.location.pathname}?${searchQuery.toString()}`;
                        window.history.pushState({}, '', newURL);

                        dataLayer.push({
                            'event': 'Suggestion',
                            'pagePath': window.location.toString(),
                            'pageTitle': `${document.querySelector('title').innerText} - ${query} Suggestion`,
                        });
                    });
            },
            processSuggestions: function (results) {
                let suggestions = [];

                if ('spelling_suggestion' in results.info.page && results.record_count === 0) {
                    suggestions.push(results.info.page.spelling_suggestion.text);
                }

                results.records.page = results.records.page.filter(record => {
                    return record.type.includes('digital');
                });

                suggestions = results.records.page.reduce((carry, record) => {
                    if ('sections' in record.highlight) {
                        const sectionTexts = this.tokenise(record.highlight.sections);

                        sectionTexts.forEach(t => {
                            if (t.split(' ').length <= 6) {
                                carry.push(this.decodeHTMLEntity(t));
                            }
                        });
                    }

                    if ('internal_title' in record.highlight) {
                        const internalTitleTexts = this.tokenise(record.highlight.internal_title);

                        internalTitleTexts.forEach(t => {
                            if (t.split(' ').length <= 6) {
                                carry.push(this.decodeHTMLEntity(t));
                            }
                        });
                    }

                    const titleTexts = this.tokenisePlain(record.title, this.inputField);

                    titleTexts.forEach(t => {
                        if (t.split(' ').length <= 6) {
                            carry.push(this.decodeHTMLEntity(t));
                        }
                    });

                    return carry;
                }, suggestions);

                suggestions = uniq(suggestions);

                // Sort by length
                suggestions = sortBy(suggestions, suggestion => {
                    return suggestion.length;
                });

                // Only show first 10
                suggestions = suggestions.slice(0, 10);

                return suggestions;
            },
            // Decodes html entities such as &#x27; (apostrophe)
            decodeHTMLEntity(encodedString) {
                const element = document.createElement('div');
                element.innerHTML = encodedString;
                return element.textContent;
            },
            tokenise(phrase) {
                // lower the case
                let phrases = phrase.toLowerCase();

                // split by em
                phrases = phrases.split('<em>');

                // remove first one as it's junk or empty
                phrases.shift();

                return phrases.map(p => {
                    // remove the closing tag
                    p = p.replace('</em>', '');

                    // amperstand
                    p = p.replace('&amp;', '');

                    // excessive spaces
                    p = p.replace(/\s{2,}/, ' ');

                    return p.trim();
                });
            },
            tokenisePlain(phrase, q) {
                // lower the case
                let phrases = phrase.toLowerCase();

                // split by phrase
                phrases = phrases.split(q);

                // remove the first one as it's junk or empty
                phrases.shift();

                return phrases.map(p => {
                    // append the query again
                    p = q + p;

                    // amperstand
                    p = p.replace('&amp;', '');

                    // excessive spaces
                    p = p.replace(/\s{2,}/, ' ');

                    return p.trim();
                });
            },
            updateSuggestionList(suggestions) {
                this.ViewModel.SearchSuggestions = suggestions.map(suggestion => {
                    const url = new URL(this.searchPageUrl);  // '/search/?q=' + suggestion,
                    url.searchParams.append(this.searchParam, suggestion);

                    return {
                        plain: suggestion,
                        highlighted: suggestion.replace(new RegExp(this.inputField.value, 'ig'), '<strong>$&</strong>'),
                        url: url.toString(),
                        click: () => {
                            this.ViewModel.Loading = true;
                            this.ViewModel.SearchSuggestions = [];
                            this.ViewModel.SearchQuery = suggestion;
                            this.query = suggestion;
                            this.updateSearch(true);
                            this.updateFilters();
                        }
                    }
                });
            },
            updateSearch(track) {
                if (!this.ViewModel.SearchQuery) {
                    return (this.ViewModel.Loading = false);
                }
                const promises = [
                    this.getResults(this.ViewModel.SearchQuery, track),
                ];
                Promise.all(promises)
                    .then(data => {
                        this.updateInput();

                        if (data[0].record_count) {
                            return this.showResults(data[0]);
                        }

                        this.ViewModel.Loading = false;
                    });
            },
            showResults: function (data) {

                this.buildPagination(data);

                this.processPageResults(data.records.page);
                this.filtersChanged([]);

                this.ViewModel.Loading = false;
            },
            processPageResults: function (results) {
                results = results.filter(result => {
                    return result.type.includes('digital');
                });

                results = results.filter(result => {
                    return this.ViewModel.Performances.some(performance => performance.id === result.internal_id);
                  }).map(result => {
                    const matchedPerformance = this.ViewModel.Performances.find(performance => performance.id === result.internal_id);

                    if (matchedPerformance) {
                        return { ...result, ...matchedPerformance };
                      }
                  });

                this.updateResults();
                this.results = results;
                this.updateFilters();
            },
            buildPagination: function (data) {
                const {
                    total_result_count, per_page, current_page, num_pages, query
                } = data.info.page;

                const start = (current_page - 1) * per_page;

                // index of last item on this page
                const end = Math.min((current_page * per_page) - 1, total_result_count);

                // link to previous page, if exists
                const prev = current_page > 1 ? this.makeUrlWithPage(query, current_page - 1) : null;

                // link to next page, if exists
                const next = current_page < num_pages ? this.makeUrlWithPage(query,  current_page + 1) : null;

                let pages = [];

                for (let i = 1; i <= num_pages; i++) {
                    // skip showing links to pages that aren't either:
                    //  * the first few pages
                    //  * the last page
                    //  * within a range of current page
                    if (i > 1 && i < num_pages && Math.abs(i - current_page) > 3) {
                        continue;
                    }

                    // the index of the first result on the given page
                    let offset = i * per_page;

                    pages.push({
                        number: i,
                        min: offset,
                        max: Math.min(total_result_count, offset + per_page),
                        is_current: i == current_page,
                        link: this.makeUrlWithPage(query, i)
                    });
                }
                let lastPage = 0;
                pages.forEach(page => {
                    // `jumped` is true if there where skipped pages (use to add ellipse via CSS)
                    page.jumped = (lastPage !== (page.number - 1));
                    lastPage = page.number;
                });

                this.ViewModel.CurrentPage = current_page;
                this.ViewModel.TotalPages = num_pages;
                this.ViewModel.TotalResults = total_result_count;

                this.ViewModel.Pagination = { start, end, prev, next, pages };
            },
            makeUrlWithPage: function (query, page) {
                if (!query) {
                    return '';
                }

                query = query
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');

                page = parseInt(page, 10);

                let params = new URLSearchParams();

                params.append(this.searchParam, query);

                if (page) {
                    params.append(this.page, page);
                }

                const separator = this.searchPageUrl.indexOf('?') > -1 ? '&' : '?';
                return this.searchPageUrl + separator + params.toString();
            },
            getResults: function (query, track) {
                const url = this.makeSwiftypeUrl(query);

                return fetchPolyfill(url)
                    .then(response => response.json())
                    .finally(() => {
                        if (track) {
                            const searchQuery = new URLSearchParams(document.location.search);
                            searchQuery.set(this.searchParam, query);
                            if (this.page > 1) {
                                searchQuery.set('page', this.page);
                            } else {
                                searchQuery.delete('page');
                            }
                            const newURL = `${window.location.origin}${window.location.pathname}?${searchQuery.toString()}`;
                            window.history.pushState({}, '', newURL);

                            dataLayer.push({
                                'event': 'Pageview',
                                'pagePath': window.location.toString(),
                                'pageTitle': `${document.querySelector('title').innerText} - ${query}`,
                            });
                        }
                    });
            },
            makeSwiftypeUrl: function (query) {
                let params = {
                    q: query,
                    per_page: this.resultsPerPage,
                    spelling: 'always',
                    engine_key: this.engineKey,
                }

                if (this.hasPage()) {
                    params['page'] = this.getPage();
                }

                params = new URLSearchParams(params);

                params.append('highlight_fields[page][body][size]', 275 + 25); // we'll trim to 275, but give it a buffer
                params.append('highlight_fields[page][body][fallback]', true);

                // Makes use of the `internal_weight` during search (this means that results with higher weights will appear on earlier pages)
                // See (https://swiftype.com/documentation/site-search/searching/boosting)
                params.append('functional_boosts[page][internal_weight]', 'linear');

                return 'https://api.swiftype.com/api/v1/public/engines/search.json?' + params.toString();
            },
            getQueryParams: function () {
                return new URLSearchParams(window.location.search);
            },
            hasPage: function () {
                return this.getQueryParams().has(this.page);
            },
            getPage: function (defaultValue) {
                return parseInt(this.getQueryParams().get(this.page), 10) || defaultValue || 1;
            },
            updateInput: function () {
                this.inputField.value = this.ViewModel.SearchQuery;
            },
            perfDateId: function (perf) {
                // Need to be careful about duplicate Ids :/
                return `date-${perf.first_aired}`;
            },
            getSearchPageURL: function (page, query) {
                if (!query) {
                    // If query not set then use current query
                    const search = new URLSearchParams(document.location.search);
                    query = search.get(this.searchParam);
                }
                return `${document.location.pathname}?page=${page}&${this.searchParam}=${query}`;
            },
            getSearchURL: function (query, filter) {
                const url = new URL('https://api.swiftype.com/api/v1/public/engines/suggest.json');
                url.searchParams.set('q', query);
                url.searchParams.set('page', this.page);
                url.searchParams.set('per_page', 15);
                url.searchParams.set('spelling', 'always');
                url.searchParams.set('engine_key', this.engineKey);

                url.searchParams.set('highlight_fields[page][body][size]', '300');
                url.searchParams.set('highlight_fields[page][body][fallback]', 'true');
                url.searchParams.set('functional_boosts[page][internal_weight]', 'linear');

                if (filter) {
                    url.searchParams.set('filters[page][type][]', filter);
                } else if (this.filter && this.filter !== 'all') {
                    url.searchParams.set('filters[page][type][]', this.filter);
                }

                return url.toString();
            },
            doNewSearch: function () {
                // Just resets the page to 1 and runs the search (assumes the query has changed)
                this.page = 1;
                this.ViewModel.AllFilteredResults = this.ViewModel.Performances;
                this.ViewModel.Loading = true;
                this.ViewModel.SearchQuery = this.query;
                this.updateSearch(false);
                this.updateFilters();
                this.updateResults();
            },
        },
    }
)
